/**
 * WHICH POOL READER IS THIS PHYSICAL READER — by serial number, and nothing else.
 *
 * The inventory unit and the location's EMV pool record are two records of one
 * box, and the serial is the only field both carry. Screens used to fall back
 * to "the only reader in the pool" (or "the only free one") when the serial did
 * not match. On this estate pools carry dead readers and serials are not
 * unique, so that guess bound tills to the wrong reader. It is gone: a match is
 * an exact, trimmed serial match to exactly ONE pool record, and anything else
 * comes back as a sentence that names the serial it looked for.
 */
export type ReaderMatch<T> =
  | { device: T; problem?: undefined }
  | { device: null; problem: string };

export function readerBySerial<T extends { serialNumber?: string | null }>(
  pool: readonly T[] | null | undefined,
  serial: string | null | undefined,
): ReaderMatch<T> {
  const want = String(serial ?? '').trim();
  if (!want) {
    return {
      device: null,
      problem: 'The reader has no serial number recorded, so no card reader in the pool can be matched to it — add its serial first.',
    };
  }
  const hits = (pool || []).filter((d) => String(d?.serialNumber ?? '').trim() === want);
  if (hits.length === 1) return { device: hits[0]! };
  if (hits.length === 0) {
    return {
      device: null,
      problem: `No card reader with serial ${want} is in this location’s pool — it is not registered at this location yet. Register it on the Card processing step first; nothing was bound.`,
    };
  }
  return {
    device: null,
    problem: `${hits.length} card readers at this location carry serial ${want} — remove the wrong one from the pool first; nothing was bound.`,
  };
}

/** The tender a reader enrolled for this transport can actually take. */
export const TENDER_FOR_CLASS: Record<string, string> = {
  dejavoo_spin_usb: 'credit_dejavoo',
  dejavoo_spin_cloud: 'credit_dejavoo_cloud',
  dejavoo_rubytill_pay: 'credit_dejavoo_rtpay',
};
